import PropTypes from 'prop-types';
import { CityPropType, NewBookingDeliveryPropType } from 'common/prop-types';
import React, { useCallback, useMemo } from 'react';
import { withRouter } from 'react-router';
import { Button, Icon, Col, Drawer } from 'grab-ui';
import _get from 'lodash/get';
import I18n from 'i18n-js';
import calcPrice from 'common/utils/calc-price';
import formatCurrency from 'common/utils/format-currency';
import { getQuoteAmountDefaultText } from 'helper/quote';
import {
  newBookingPageStateKey,
  newBookingPageStateModeValues,
  ROUTES_V2_PATHS,
} from 'constants/route-constants';
import { ExceptMobile, Mobile } from 'components/v2/responsive';
import ScribeDuck from 'ducks/scribe-duck';
import styles from './styles.less';

const CTAButton = ({
  delivery,
  city,
  history,
  detailOverlayVisible,
  dispatch,
}) => {
  const { detail: deliveryDetail, quote } = delivery;

  const onAddDeliveryDetails = useCallback(
    () => {
      if (deliveryDetail) {
        dispatch(
          ScribeDuck.creators.scribeExpressHomePageWebConfirmBooking(
            deliveryDetail,
          ),
        );
      }

      history.push(ROUTES_V2_PATHS.newBooking, {
        [newBookingPageStateKey.INIT_MODE]:
          newBookingPageStateModeValues.SINGLE,
      });
    },
    [history, deliveryDetail, dispatch],
  );

  const { ctaDisabled, cta, price } = useMemo(
    () => {
      const pickUp = _get(deliveryDetail, 'pickUp.poi');
      const dropOff = _get(deliveryDetail, 'dropOffs[0].poi');
      const serviceType = _get(deliveryDetail, 'configuration.serviceType');
      const scheduledPickupSlot = _get(
        deliveryDetail,
        'configuration.scheduledPickupSlot',
      );
      const currency = _get(quote, 'currency');
      const quoteSignature = _get(quote, 'signature');

      if (quoteSignature && pickUp && dropOff) {
        const fareLowerBound = _get(quote, 'finalFare.lowerBound', 0);
        const amount = calcPrice(fareLowerBound, currency);
        if (serviceType && scheduledPickupSlot !== undefined) {
          return {
            ctaDisabled: false,
            cta: I18n.t('v2.homepage.manualOrder.button.addOrderDetail'),
            price: formatCurrency({ code: currency.code, amount }),
          };
        }
        return {
          ctaDisabled: true,
          cta: I18n.t('v2.anytimeBooking.estimatedPrice'),
          price: formatCurrency({ code: currency.code, amount }),
        };
      }
      return {
        ctaDisabled: true,
        cta: I18n.t('v2.anytimeBooking.estimatedPrice'),
        price: getQuoteAmountDefaultText(city.countryCode),
      };
    },
    [deliveryDetail, quote, city],
  );

  return (
    <React.Fragment>
      <ExceptMobile>
        <Col xs={24} md={4}>
          <Button
            type="primary"
            disabled={ctaDisabled}
            className={styles.ctaButton}
            onClick={onAddDeliveryDetails}
          >
            <div className={styles.childWrap}>
              <div>
                <h6> {cta} </h6>
                <h5> {price} </h5>
              </div>
              <Icon type="right" />
            </div>
          </Button>
        </Col>
      </ExceptMobile>
      <Mobile>
        <Drawer
          placement="bottom"
          width="100%"
          visible={!ctaDisabled && !detailOverlayVisible}
          height={120}
          closable={false}
          mask={false}
          className={styles.mobileCTAPanel}
        >
          <div className={styles.priceTag}>
            <span> {I18n.t('v2.anytimeBooking.estimatedPrice')} </span>
            <span className={styles.price}> {price} </span>
          </div>
          <Button
            className={styles.mobileCTAButton}
            onClick={onAddDeliveryDetails}
            disabled={ctaDisabled}
            type="primary"
          >
            {I18n.t('v2.homepage.manualOrder.button.addOrderDetail')}
          </Button>
        </Drawer>
      </Mobile>
    </React.Fragment>
  );
};

CTAButton.propTypes = {
  history: PropTypes.shape({}).isRequired,
  city: CityPropType.isRequired,
  delivery: NewBookingDeliveryPropType.isRequired,
  detailOverlayVisible: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
};

CTAButton.defaultProps = {
  detailOverlayVisible: false,
};

export default withRouter(CTAButton);
